import {
  MarketDetail,
  EventDetail,
  FollowMarketInfo,
  MarketOrderInfo,
  OptionPriceSnapshot,
  OptionsTimeInterval,
  OrderSide,
  UserOperation,
  UserOrderInfo,
  UserPositionInfo,
  ApiResponse,
  MarketsSortType,
  OmitFirstArg,
  MarketStatus,
  BaseFetchParams,
  HoldingMarket,
  BannerType,
  SectionType,
  Market,
  TimeInterval,
  LeaderboardSortType,
  LeaderboardEntry,
} from '../types';
import ky, { KyInstance, Options } from 'ky';

export type FollowMarketParams = BaseFetchParams;
export type FollowMarketBody = {
  marketAddress: string;
};
/**
 * follow market
 * (need auth)
 * @param api The KyInstance
 * @param marketAddress
 * @param options
 * @returns
 * @example
 * const res = await followMarket('0x1234567890123456789012345678901234567890');
 */
export const followMarket = async (
  api: KyInstance = ky,
  searchParams: FollowMarketsParams,
  json: FollowMarketBody,
  options?: Options
) => {
  return await api.post<ApiResponse>('follow-market', { ...options, searchParams, json }).json();
};

export type UnfollowMarketParams = BaseFetchParams;
export type UnfollowMarketBody = {
  marketAddress: string;
};
/**
 * unfollow market
 * (need auth)
 * @param api The KyInstance
 * @param marketAddress
 * @param options
 * @returns
 * @example
 * const res = await unfollowMarket('0x1234567890123456789012345678901234567890');
 */
export const unfollowMarket = async (
  api: KyInstance = ky,
  searchParams: UnfollowMarketParams,
  json: UnfollowMarketBody,
  options?: Options
) => {
  return await api.post<ApiResponse>('unfollow-market', { ...options, searchParams, json }).json();
};

export type FollowMarketsParams = BaseFetchParams & {
  page?: number;
  pageSize?: number;
};

export type FollowMarkets = {
  total: number;
  marketList: FollowMarketInfo[];
};

/**
 * get followed markets list
 * (need auth)
 * @param api The KyInstance
 * @param searchParams
 * @param options
 * @returns
 * @example
 * const res = await getFollowMarkets({ page: 1, pageSize: 10 });
 */
export const getFollowMarkets = async (api: KyInstance = ky, searchParams: FollowMarketsParams, options?: Options) => {
  return await api.get<ApiResponse<FollowMarkets>>('get-follow-markets', { ...options, searchParams }).json();
};

export type HoldingPositionsMarketParams = BaseFetchParams & {
  uid: string;
  page?: number;
  pageSize?: number;
};

export type HoldingPositionsMarket = {
  total: number;
  totalValue: string;
  markets: HoldingMarket[];
};

/**
 * get markets with holding positions
 * (not auth)
 * @param api The KyInstance
 * @param searchParams
 * @param options
 * @returns
 * @example
 * const res = await getHoldingPositionsMarket({ uid: 'promethex...', page: 1, pageSize: 10 });
 */
export const getHoldingPositionsMarket = async (
  api: KyInstance = ky,
  searchParams: HoldingPositionsMarketParams,
  options?: Options
) => {
  return await api
    .get<ApiResponse<HoldingPositionsMarket>>('get-holding-positions-market', {
      ...options,
      searchParams,
    })
    .json();
};

export type HotMarketsParams = BaseFetchParams;

export type HotMarkets = {
  marketList: FollowMarketInfo[];
};

/**
 * get hot markets list
 * (not auth)
 * @param api The KyInstance
 * @param options
 * @returns
 * @example
 * const res = await getHotMarkets({ baseTokenAddress: '0x...' });
 */
export const getHotMarkets = async (api: KyInstance = ky, searchParams: HotMarketsParams, options?: Options) => {
  return await api.get<ApiResponse<HotMarkets>>('get-hot-markets', { ...options, searchParams }).json();
};

export type MarketDetailParams = Partial<BaseFetchParams> & {
  marketAddress: string;
};
/**
 * get market details
 * (not auth)
 * @param api The KyInstance
 * @param marketAddress
 * @param options
 * @returns
 * @example
 * const res = await getMarketDetail('0x1234567890123456789012345678901234567890');
 */
export const getMarketDetail = async (api: KyInstance = ky, searchParams: MarketDetailParams, options?: Options) => {
  return await api.get<ApiResponse<MarketDetail>>('get-market-detail', { ...options, searchParams }).json();
};

export type EventDetailParams = {
  id: string;
};

/**
 * get event detail
 * (not auth)
 * @param api The KyInstance
 * @param searchParams
 * @param options
 * @returns
 * @example
 * const res = await getEventDetail({ id: 'event-id' });
 */
export const getEventDetail = async (api: KyInstance = ky, searchParams: EventDetailParams, options?: Options) => {
  return await api.get<ApiResponse<EventDetail>>('get-event-detail', { ...options, searchParams }).json();
};

export type EventsListParams = BaseFetchParams & {
  page?: number;
  pageSize?: number;
  status?: number;
};

export const getEvents = async (api: KyInstance = ky, searchParams: EventsListParams, options?: Options) => {
  return await api.get<ApiResponse<{ total: number; events: import('../types/market').EventListItem[] }>>('get-events', { ...options, searchParams }).json();
};

export type MarketTradesParams = BaseFetchParams & {
  marketAddress: string;
  page?: number;
  pageSize?: number;
};

export type MarketTrades = {
  total: number;
  orders: MarketOrderInfo[];
};

/**
 * get market trades
 * (not auth)
 * @param api The KyInstance
 * @param searchParams
 * @param options
 * @returns
 * @example
 * const res = await getMarketTrades({ marketAddress: '0x1234567890123456789012345678901234567890', page: 1, pageSize: 10 });
 */
export const getMarketTrades = async (api: KyInstance = ky, searchParams: MarketTradesParams, options?: Options) => {
  return await api.get<ApiResponse<MarketTrades>>('get-market-trades', { ...options, searchParams }).json();
};

export type UserPositionsParams = Partial<BaseFetchParams> & {
  page: number;
  pageSize: number;
  uid?: string;
  marketAddress?: string;
  optionAddress?: string;
};

export type UserAssetsInfoParams = BaseFetchParams & {
  uid: string;
};

export type UserAssetsInfo = {
  value: string;
  balance: string;
  portfolio: string;
  pnl: string;
  decimal: number;
  volume: string;
  pnlRank: number;
};

/**
 * Get user assets info
 * (need auth)
 * @param api The KyInstance
 * @param {string} baseTokenAddress The base token address.
 * @param options
 * @returns The user assets info response.
 * @example
 * const response = await getUserAssetsInfo({baseTokenAddress:0xx});
 */
export const getUserAssetsInfo = async (api: KyInstance = ky, searchParams: UserAssetsInfoParams, options?: Options) => {
  return await api.get<ApiResponse<UserAssetsInfo>>('get-user-asset-info', { ...options, searchParams }).json();
};

export type UserPositions = {
  total: number;
  positions: UserPositionInfo[];
};

/**
 * get user positions
 * (need auth)
 * @param api The KyInstance
 * @param searchParams
 * @param options
 * @returns
 * @example
 * const res = await getUserPositions({ page: 1, pageSize: 10 });
 */
export const getUserPositions = async (api: KyInstance = ky, searchParams: UserPositionsParams, options?: Options) => {
  return await api.get<ApiResponse<UserPositions>>('get-user-positions', { ...options, searchParams }).json();
};

export type UserTradesParams = BaseFetchParams & {
  page: number;
  pageSize: number;
  uid?: string;
  marketAddress?: string;
};

export type UserTrades = {
  total: number;
  orders: UserOrderInfo[];
};

/**
 * get user trades
 * (need auth)
 * @param api The KyInstance
 * @param searchParams
 * @param options
 * @returns
 * @example
 * const res = await getUserTrades({ page: 1, pageSize: 10 });
 */
export const getUserTrades = async (api: KyInstance = ky, searchParams: UserTradesParams, options?: Options) => {
  return await api.get<ApiResponse<UserTrades>>('get-user-trades', { ...options, searchParams }).json();
};

export type PlaceOrderParams = BaseFetchParams;
export type PlaceOrderBody = {
  marketAddress: string;
  optionAddress: string;
  side: OrderSide;
  price: string;
  amount: string;
  minReceiveAmount: string;
  deadline: string;
  userOperation: UserOperation;
};

export type PlaceOrderApiResult = {
  opHash: string;
  orderUuid: string;
};

/**
 * place order
 * (need auth)
 * @param api The KyInstance
 * @param body
 * @param options
 * @returns
 * @example
 * const res = await placeOrder({
 *   marketAddress: '0x1234567890123456789012345678901234567890',
 *   optionAddress: '0x1234567890123456789012345678901234567890',
 *   side: OrderSide.deposit,
 *   price: '1',
 *   amount: '1',
 *   minReceiveAmount: '1',
 *   deadline: '1',
 *   userOperation: { ... }
 * });
 */
export const placeOrder = async (
  api: KyInstance = ky,
  searchParams: PlaceOrderParams,
  json: PlaceOrderBody,
  options?: Options
) => {
  return await api.post<ApiResponse<PlaceOrderApiResult>>('place-order', { ...options, searchParams, json }).json();
};

export type ClaimMarketParams = BaseFetchParams;

export type ClaimMarketBody = {
  marketAddress: string;
  optionAddress: string;
  userOperation: UserOperation;
};

type ClaimMarketApiResult = {
  opHash: string;
};

/**
 * claim market result
 * (need auth)
 * @param api The KyInstance
 * @param body
 * @param options
 * @returns opHash
 * @example
 * const opHash = await claimMarket({
 *   marketAddress: '0x1234567890123456789012345678901234567890',
 *   optionAddress: '0x1234567890123456789012345678901234567890',
 *   userOperation: { ... }
 * });
 */
export const claimMarket = async (
  api: KyInstance = ky,
  searchParams: ClaimMarketParams,
  json: ClaimMarketBody,
  options?: Options
) => {
  return await api.post<ApiResponse<ClaimMarketApiResult>>('claim-market-result', { ...options, searchParams, json }).json();
};

export type MarketOptionPriceHistoryParams = BaseFetchParams & {
  marketAddress: string;
  timeInterval: OptionsTimeInterval;
};

export type MarketOptionPriceHistory = {
  total: number;
  snapshots: OptionPriceSnapshot[];
};

/**
 * get market option price history
 * (not auth)
 * @param api The KyInstance
 * @param searchParams
 * @param options
 * @returns
 * @example
 * const res = await getMarketOptionPriceHistory({
 *   marketAddress: '0x1234567890123456789012345678901234567890',
 *   timeInterval: OptionsTimeInterval.DAY
 * });
 */
export const getMarketOptionPriceHistory = async (
  api: KyInstance = ky,
  searchParams: MarketOptionPriceHistoryParams,
  options?: Options
) => {
  return await api
    .get<ApiResponse<MarketOptionPriceHistory>>('get-market-option-price-history', { ...options, searchParams })
    .json();
};

export type MarketsParams = BaseFetchParams & {
  uid?: string;
  tag?: string;
  /** Currency lens: scope results to markets settling in this base token
   *  (server-side filter pending; ignored by backends that predate it) */
  baseTokenAddress?: string;
  status?: MarketStatus;
  sortType?: MarketsSortType;
  hotWords?: string;
  categoryId?: string;
  sortAsc?: boolean;
  followed?: boolean;
  minVolumeTrending?: boolean;
  soonExpired?: boolean;
  newMarket?: boolean;
  page: number;
  pageSize: number;
};

export type Markets = {
  totalCount: number;
  markets: Market[];
};

/**
 * get markets
 * (not auth)
 * @param api The KyInstance
 * @param searchParams
 * @param options
 * @returns
 * @example
 * const res = await getMarkets({
 *   uid: '0x1234567890123456789012345678901234567890',
 *   tag: 0,
 *   sortType: MarketsSortType.createTime,
 *   page: 1,
 *   pageSize: 10
 * });
 */
export const getMarkets = async (api: KyInstance = ky, searchParams: MarketsParams, options?: Options) => {
  return await api.get<ApiResponse<Markets>>('get-markets', { ...options, searchParams }).json();
};

export type TagsParams = BaseFetchParams & {
  page: number;
  pageSize: number;
};
export type TagResponse = {
  total: number;
  tags: string[];
};
/**
 * get tags
 * (not auth)
 * @param api The KyInstance
 * @param searchParams
 * @param options
 * @returns
 * @example
 * const res = await getTags({ page: 1, pageSize: 10 });
 */
export const getTags = async (api: KyInstance = ky, searchParams: TagsParams, options?: Options) => {
  return await api.get<ApiResponse<TagResponse>>('get-tags', { ...options, searchParams }).json();
};

export type RecordOrderParams = BaseFetchParams;
export type RecordOrderBody = {
  txHash: string;
  marketAddress: string;
  optionAddress: string;
  side: OrderSide;
  amount: string;
  price: string;
};

export type RecordOrderResult = {
  orderUuid: string;
};

/**
 * Record an on-chain order (after direct writeContract)
 * (need auth)
 * @param api The KyInstance
 * @param searchParams
 * @param json
 * @param options
 * @returns
 */
export const recordOrder = async (
  api: KyInstance = ky,
  searchParams: RecordOrderParams,
  json: RecordOrderBody,
  options?: Options
) => {
  return await api.post<ApiResponse<RecordOrderResult>>('record-order', { ...options, searchParams, json }).json();
};

export type CategoriesParams = BaseFetchParams;

export type Category = {
  id: string;
  name: string;
  weight: number;
};

export type CategoryResult = {
  total: number;
  categories: Category[];
};

export const getCategories = async (api: KyInstance = ky, searchParams: CategoriesParams, options?: Options) => {
  return await api.get<ApiResponse<CategoryResult>>('get-categories', { ...options, searchParams }).json();
};

export type SectionPrediction = {
  prediction: string;
  type?: 'event' | 'market'; // absent = 'market' (backward compat)
  weight: number;
  id: string;
};
export type Section = {
  color: string;
  title: string;
  type: SectionType;
  weight: number;
  id: string;
  predictions: SectionPrediction[];
};
export type SectionsResult = { sections: Section[] };

export type SectionsParams = BaseFetchParams;
/**
 * get sections
 * (not auth)
 * @param searchParams
 * @param options
 * @returns
 * @example
 * const res = await getSections({ page: 1, pageSize: 10 });
 */
export const getSections = async (api: KyInstance = ky, searchParams: SectionsParams, options?: Options) => {
  return await api.get<ApiResponse<SectionsResult>>('get-sections', { ...options, searchParams }).json();
};

export type Banner = {
  weight: number;
  image: string;
  type: BannerType;
  url: string;
  id: string;
};

export type BannersParams = BaseFetchParams;

export type BannerResult = { banners: Banner[] };

export const getBanners = async (api: KyInstance = ky, searchParams: BannersParams, options?: Options) => {
  return await api.get<ApiResponse<BannerResult>>('get-banners', { ...options, searchParams }).json();
};

export type LeaderboardParams = BaseFetchParams & {
  uid?: string;
  timeInterval: TimeInterval;
  sortType: LeaderboardSortType;
  page: number;
  pageSize: number;
};

export type LeaderboardResult = { total: number; entries: LeaderboardEntry[]; userOwnEntry?: LeaderboardEntry };

export const getLeaderboard = async (api: KyInstance = ky, searchParams: LeaderboardParams, options?: Options) => {
  return await api.get<ApiResponse<LeaderboardResult>>('get-leaderboard', { ...options, searchParams }).json();
};

export type MarketApi = {
  /**
   * follow market
   * (need auth)
   * @param marketAddress
   * @param options
   * @returns
   * @example
   * const res = await followMarket('0x1234567890123456789012345678901234567890');
   */
  followMarket: OmitFirstArg<typeof followMarket>;

  /**
   * unfollow market
   * (need auth)
   * @param marketAddress
   * @param options
   * @returns
   * @example
   * const res = await unfollowMarket('0x1234567890123456789012345678901234567890');
   */
  unfollowMarket: OmitFirstArg<typeof unfollowMarket>;

  /**
   * get followed markets list
   * (need auth)
   * @param searchParams
   * @param options
   * @returns
   * @example
   * const res = await getFollowMarkets({ page: 1, pageSize: 10 });
   */
  getFollowMarkets: OmitFirstArg<typeof getFollowMarkets>;

  /**
   * get markets with holding positions
   * (not auth)
   * @param searchParams
   * @param options
   * @returns
   * @example
   * const res = await getHoldingPositionsMarket({ page: 1, pageSize: 10 });
   */
  getHoldingPositionsMarket: OmitFirstArg<typeof getHoldingPositionsMarket>;

  /**
   * get hot markets list
   * (not auth)
   * @param options
   * @returns
   * @example
   * const res = await getHotMarkets();
   */
  getHotMarkets: OmitFirstArg<typeof getHotMarkets>;

  /**
   * get market details
   * (not auth)
   * @param marketAddress
   * @param options
   * @returns
   * @example
   * const res = await getMarketDetail('0x1234567890123456789012345678901234567890');
   */
  getMarketDetail: OmitFirstArg<typeof getMarketDetail>;

  /**
   * get event detail
   * (not auth)
   * @param searchParams
   * @param options
   * @returns
   * @example
   * const res = await getEventDetail({ id: 'event-id' });
   */
  getEventDetail: OmitFirstArg<typeof getEventDetail>;
  getEvents: OmitFirstArg<typeof getEvents>;

  /**
   * get market trades
   * (not auth)
   * @param searchParams
   * @param options
   * @returns
   * @example
   * const res = await getMarketTrades({ marketAddress: '0x1234567890123456789012345678901234567890', page: 1, pageSize: 10 });
   */
  getMarketTrades: OmitFirstArg<typeof getMarketTrades>;

  /**
   * Get user assets info
   * (need auth)
   * @param {string} baseTokenAddress The base token address.
   * @param options
   * @returns The user assets info response.
   * @example
   * const response = await getUserAssetsInfo('0x...');
   */
  getUserAssetsInfo: OmitFirstArg<typeof getUserAssetsInfo>;

  /**
   * get user positions
   * (no auth)
   * @param searchParams
   * @param options
   * @returns
   * @example
   * const res = await getUserPositions({ page: 1, pageSize: 10 });
   */
  getUserPositions: OmitFirstArg<typeof getUserPositions>;

  /**
   * get user trades
   * (need auth)
   * @param searchParams
   * @param options
   * @returns
   * @example
   * const res = await getUserTrades({ page: 1, pageSize: 10 });
   */
  getUserTrades: OmitFirstArg<typeof getUserTrades>;

  /**
   * place order
   * (need auth)
   * @param body
   * @param options
   * @returns
   * @example
   * const res = await placeOrder({
   *   marketAddress: '0x1234567890123456789012345678901234567890',
   *   optionAddress: '0x1234567890123456789012345678901234567890',
   *   side: OrderSide.deposit,
   *   price: '1',
   *   amount: '1',
   *   minReceiveAmount: '1',
   *   deadline: '1',
   *   userOperation: { ... }
   * });
   */
  placeOrder: OmitFirstArg<typeof placeOrder>;

  /**
   * claim market result
   * (need auth)
   * @param body
   * @param options
   * @returns opHash
   * @example
   * const opHash = await claimMarket({
   *   marketAddress: '0x1234567890123456789012345678901234567890',
   *   optionAddress: '0x1234567890123456789012345678901234567890',
   *   userOperation: { ... }
   * });
   */
  claimMarket: OmitFirstArg<typeof claimMarket>;

  /**
   * get market option price history
   * (not auth)
   * @param searchParams
   * @param options
   * @returns
   * @example
   * const res = await getMarketOptionPriceHistory({
   *   marketAddress: '0x1234567890123456789012345678901234567890',
   *   timeInterval: OptionsTimeInterval.DAY
   * });
   */
  getMarketOptionPriceHistory: OmitFirstArg<typeof getMarketOptionPriceHistory>;

  /**
   * get markets
   * (not auth)
   * @param searchParams
   * @param options
   * @returns
   * @example
   * const res = await getMarkets({
   *   uid: '0x1234567890123456789012345678901234567890',
   *   tag: 0,
   *   sortType: MarketsSortType.createTime,
   *   page: 1,
   *   pageSize: 10
   * });
   */
  getMarkets: OmitFirstArg<typeof getMarkets>;

  /**
   * get tags
   * (not auth)
   * @param searchParams
   * @param options
   * @returns
   * @example
   * const res = await getTags({
   *   page: 1,
   *   pageSize: 10
   * });
   */
  getTags: OmitFirstArg<typeof getTags>;

  /**
   * get categories
   * (not auth)
   * @param options
   * @returns
   * @example
   * const res = await getCategories();
   */
  getCategories: OmitFirstArg<typeof getCategories>;

  /**
   * get sections
   * (not auth)
   * @param options
   * @returns
   * @example
   * const res = await getSections();
   */
  getSections: OmitFirstArg<typeof getSections>;

  /**
   * get banners
   * (not auth)
   * @param searchParams
   * @param options
   * @returns
   * @example
   * const res = await getBanners();
   */
  getBanners: OmitFirstArg<typeof getBanners>;

  /**
   * get leaderboard
   * (not auth)
   * @param searchParams
   * @param options
   * @returns
   * @example
   * const res = await getLeaderboard();
   */
  getLeaderboard: OmitFirstArg<typeof getLeaderboard>;

  /**
   * Record an on-chain order (after direct writeContract)
   * (need auth)
   * @param searchParams
   * @param json
   * @param options
   * @returns
   */
  recordOrder: OmitFirstArg<typeof recordOrder>;
};

// Fix the createMarketApi function with proper error handling and consistent naming
export const createMarketApi = (initOptions: Options): MarketApi => {
  if (!initOptions.prefixUrl) {
    throw new Error('prefixUrl is required in initOptions');
  }
  const mergedOptions: Options = {
    ...initOptions,
    prefixUrl: `${initOptions.prefixUrl}/market`,
  };
  const marketApi = ky.create(mergedOptions);
  return {
    followMarket: followMarket.bind(null, marketApi),
    unfollowMarket: unfollowMarket.bind(null, marketApi),
    getFollowMarkets: getFollowMarkets.bind(null, marketApi),
    getHoldingPositionsMarket: getHoldingPositionsMarket.bind(null, marketApi),
    getHotMarkets: getHotMarkets.bind(null, marketApi),
    getMarketDetail: getMarketDetail.bind(null, marketApi),
    getEventDetail: getEventDetail.bind(null, marketApi),
    getEvents: getEvents.bind(null, marketApi),
    getMarketTrades: getMarketTrades.bind(null, marketApi),
    getUserAssetsInfo: getUserAssetsInfo.bind(null, marketApi),
    getUserPositions: getUserPositions.bind(null, marketApi),
    getUserTrades: getUserTrades.bind(null, marketApi),
    claimMarket: claimMarket.bind(null, marketApi),
    getMarketOptionPriceHistory: getMarketOptionPriceHistory.bind(null, marketApi),
    getMarkets: getMarkets.bind(null, marketApi),
    getTags: getTags.bind(null, marketApi),
    placeOrder: placeOrder.bind(null, marketApi),
    getCategories: getCategories.bind(null, marketApi),
    getSections: getSections.bind(null, marketApi),
    getBanners: getBanners.bind(null, marketApi),
    getLeaderboard: getLeaderboard.bind(null, marketApi),
    recordOrder: recordOrder.bind(null, marketApi),
  };
};

export default createMarketApi;
